// Thống kê: byDay dạng cột chồng (SVG thuần), byBundle/byBot bảng; lọc thời gian; super lọc theo Boss (danh sách từ /api/users — gói J; lỗi thì rơi về chủ trong bảng bot).
// V9 (rà soát 05/10/2026): tách CHẠY THẬT / CHẠY THỬ (tỉ lệ xong không còn trộn hai loại; lọc theo chế độ) và khối "Việc thật bot đã làm"
// (bình luận / trả lời / bài đăng / nhóm đã vào, theo mạng — đếm từ Việc đã làm) — câu "bot đã làm được gì cho tôi". Trục ngày dd/MM.
import { api, buildQuery, errMsg } from "../api";
import { busy, clear, gen, h, svg } from "../dom";
import { fmtMs, fmtNgay, fmtNgayNgan, isoDateVN, pct, yTicks } from "../format";
import { isSuper, loadBots, store } from "../store";
import type { Stats, UserWithBots } from "../types";
import { cuoiNgayVN, dauNgayVN, kiemKhoangNgay } from "../../../../src/shared/khoang-ngay";
import { tenKb } from "../ten-kb"; // L-78: hiện TÊN kịch bản thay vì mã kb-…
import { tenBuocVi } from "../../../../src/shared/ten-ma-noi-bo";

type LoaiViec = "binh_luan" | "tra_loi" | "dang" | "tham_gia" | "khac";
type DemViec = Record<LoaiViec, number>;
interface DemCheDo { runs: number; done: number; outcomes: number }
/** Phần máy chủ thêm cho V9 (máy chủ cũ / API giả không có → khối tương ứng không hiện). */
type StatsDayDu = Stats & { byMode?: Partial<Record<"live" | "draft" | "dry", DemCheDo>>; viec?: { tong: DemViec; theoMang: ({ mang: string } & DemViec)[] } };

const SERIES: { key: "done" | "failed" | "halted" | "stopped"; label: string; color: string }[] = [
  { key: "done", label: "xong", color: "var(--ok)" },
  { key: "failed", label: "lỗi", color: "var(--err)" },
  { key: "halted", label: "bị chặn / từ chối", color: "var(--warn)" },
  { key: "stopped", label: "dừng tay", color: "var(--muted)" },
];
const VIEC: { k: LoaiViec; nhan: string; title: string }[] = [
  { k: "binh_luan", nhan: "Bình luận đã đăng", title: "Số bình luận bot đã đăng thật trong khoảng lọc" },
  { k: "tra_loi", nhan: "Trả lời đã gửi", title: "Số câu trả lời bình luận bot đã gửi thật" },
  { k: "dang", nhan: "Bài đã đăng", title: "Số bài bot đã đăng thật (nhóm, trang, trang cá nhân)" },
  { k: "tham_gia", nhan: "Nhóm đã vào", title: "Số lần bot xin vào / đã vào nhóm" },
];
const TEN_MANG: Record<string, string> = { facebook: "Facebook", tiktok: "TikTok", youtube: "YouTube", x: "X", reddit: "Reddit", khac: "Nơi khác" };

/** (đợt B3) Kịch bản học trên bot có tên mã ("dang_bai_x") → tên đọc được; kịch bản sơ đồ (kb-…) và tên thường giữ như tenKb. Mã nguyên văn nằm trong tooltip. */
function tenKbThanThien(bundleName: string): HTMLElement {
  const o = tenKb(bundleName, { lienKet: true });
  if (!/^kb-/.test(bundleName) && /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/.test(bundleName)) { o.textContent = tenBuocVi(bundleName); o.title = `Mã kịch bản: ${bundleName}`; }
  return o;
}
export async function renderStats(root: HTMLElement, _p: Record<string, string>, query: URLSearchParams): Promise<() => void> {
  const alive = gen("stats");
  try { await loadBots(); } catch { /* ô lọc bot trống */ }
  if (!alive()) return () => {};
  const today = new Date();
  const from = h("input", { type: "date", title: "Từ 0 giờ ngày này (giờ Việt Nam)", "data-testid": "s-from", value: query.get("from") ?? isoDateVN(new Date(today.getTime() - 13 * 86_400_000)) });
  const to = h("input", { type: "date", title: "Đến hết ngày này (giờ Việt Nam)", "data-testid": "s-to", value: query.get("to") ?? isoDateVN(today) });
  const botSel = h("select", { title: "Chỉ tính lần chạy của bot này", "data-testid": "s-bot" }, h("option", { value: "" }, "Mọi bot"),
    ...[...store.bots.values()].sort((a, b) => a.name.localeCompare(b.name, "vi")).map((b) => h("option", { value: b.id }, b.name)));
  // V9: lọc theo chế độ — "Chỉ dò trang" chỉ có trong chế độ kỹ thuật (Super).
  const modeSel = h("select", { title: "Chỉ tính lần chạy thật hoặc chỉ lần chạy thử", "data-testid": "s-mode" },
    h("option", { value: "" }, "Tất cả"), h("option", { value: "live" }, "Chạy thật"), h("option", { value: "draft" }, "Chạy thử (cũ)"), isSuper() ? h("option", { value: "dry" }, "Chỉ dò trang") : null);
  modeSel.value = query.get("mode") ?? "";
  if (modeSel.value !== (query.get("mode") ?? "")) modeSel.value = "";
  const owners = new Map<string, string>();
  if (isSuper()) {
    try { for (const u of await api<UserWithBots[]>("GET", "/api/users")) owners.set(u.id, u.username); } catch { /* rơi về chủ lấy từ bảng bot */ }
    if (!alive()) return () => {};
  }
  for (const b of store.bots.values()) if (!owners.has(b.ownerId)) owners.set(b.ownerId, b.ownerName);
  const ownerSel = h("select", { title: "Chỉ Super Boss: chỉ tính bot thuộc tài khoản Boss này", "data-testid": "s-owner" }, h("option", { value: "" }, "Mọi Boss"), ...[...owners].map(([id, n]) => h("option", { value: id }, n)));
  ownerSel.value = query.get("ownerId") ?? "";
  if (ownerSel.value !== (query.get("ownerId") ?? "")) ownerSel.value = "";
  botSel.value = query.get("botId") ?? "";
  if (botSel.value !== (query.get("botId") ?? "")) botSel.value = "";
  const applyBtn = h("button", { type: "button", class: "pri", title: "Tính lại thống kê theo bộ lọc bên trên", "data-testid": "s-apply" }, "Xem");
  const body = h("div");
  root.append(h("h1", {}, "Thống kê"),
    h("div", { class: "card" }, h("div", { class: "field-row" },
      h("label", { class: "f" }, h("span", {}, "Từ ngày"), from),
      h("label", { class: "f" }, h("span", {}, "Đến ngày"), to),
      h("label", { class: "f" }, h("span", {}, "Bot"), botSel),
      h("label", { class: "f" }, h("span", {}, "Chế độ"), modeSel),
      isSuper() ? h("label", { class: "f" }, h("span", {}, "Boss"), ownerSel) : null),
      h("div", { class: "row" }, applyBtn)),
    body);

  async function load(): Promise<void> {
    const g = gen("stats-load");
    clear(body);
    const sai = kiemKhoangNgay(from.value, to.value); // L-79: báo ngay bằng câu tiếng Việt, không gọi máy chủ
    if (sai) { body.append(h("div", { class: "err-box", "data-testid": "page-error" }, sai)); return; }
    body.append(h("p", { class: "muted" }, "Đang tải…"));
    try {
      const q = buildQuery({
        from: dauNgayVN(from.value),
        to: cuoiNgayVN(to.value),
        botId: botSel.value || undefined,
        mode: modeSel.value || undefined,
        ownerId: isSuper() && ownerSel.value ? ownerSel.value : undefined,
      });
      const s = await api<StatsDayDu>("GET", `/api/stats${q}`);
      if (!g() || !alive()) return;
      clear(body);
      paint(s);
    } catch (e) {
      if (!g() || !alive()) return;
      clear(body);
      body.append(h("div", { class: "err-box", "data-testid": "page-error" }, "Không tải được thống kê: ", errMsg(e)));
    }
  }

  function paint(s: StatsDayDu): void {
    const total = s.byDay.reduce((a, d) => a + d.done + d.failed + d.halted + d.stopped, 0);
    const done = s.byDay.reduce((a, d) => a + d.done, 0);
    const live = s.byMode?.live, draft = s.byMode?.draft, dry = s.byMode?.dry;
    const tiLe = (m: DemCheDo | undefined) => (m && m.outcomes ? pct(m.done / m.outcomes) : "—");
    body.append(
      h("div", { class: "row", "data-testid": "stat-tiles" },
        tile("Tổng lần chạy", String(total), "", "Tổng số lần chạy đã kết thúc trong khoảng lọc (xong, lỗi, bị chặn, dừng)"), tile("Xong", String(done), "ok", "Số lần chạy kết thúc không lỗi"),
        // Trước: "Tỉ lệ xong" trộn chạy thử + chạy thật. Nay chỉ tính chạy thật (có máy chủ mới); máy chủ cũ không tách được → tỉ lệ chung.
        s.byMode ? tile("Chạy thật xong", tiLe(live), "", "Số lần chạy THẬT xong chia số lần chạy thật đã kết thúc (không tính chạy thử)")
          : tile("Tỉ lệ xong", total ? pct(done / total) : "—", "", "Xong chia tổng lần chạy"),
        tile("Kịch bản", String(s.byBundle.length), "", "Số kịch bản khác nhau đã chạy"), tile("Bot", String(s.byBot.length), "", "Số bot đã chạy")),
      ...(s.byMode ? [h("p", { class: "small", "data-testid": "stat-che-do" },
        h("span", { title: "Bot thao tác thật trên trang" }, h("b", {}, "Chạy thật: "), `${live?.runs ?? 0} lần · ${tiLe(live)} xong`), " — ",
        h("span", { title: "Bước gửi/đăng/xoá chỉ ghi lại, không làm thật" }, h("b", {}, "Chạy thử: "), `${draft?.runs ?? 0} lần · ${tiLe(draft)} xong`),
        isSuper() && dry?.runs ? [" — ", h("span", { class: "muted" }, `Chỉ dò trang: ${dry.runs} lần`)] : null)] : []),
      ...(s.viec ? [viecThat(s.viec)] : []),
      h("div", { class: "card" }, h("h3", {}, "Lần chạy theo ngày"),
        s.byDay.length ? [chart(s.byDay), h("div", { class: "legend" }, ...SERIES.map((x) => h("span", {}, h("i", { style: `background:${x.color}` }), x.label))),
          h("details", {}, h("summary", { title: "Bấm để hiện/ẩn số liệu từng ngày dưới dạng bảng" }, "Xem dạng bảng"), h("div", { class: "tbl" }, h("table", { "data-testid": "byday-table" },
            h("thead", {}, h("tr", {}, h("th", {}, "Ngày"), ...SERIES.map((x) => h("th", {}, x.label)))),
            h("tbody", {}, ...s.byDay.map((d) => h("tr", {}, h("td", { class: "nowrap" }, fmtNgay(d.date)), ...SERIES.map((x) => h("td", {}, String(d[x.key])))))))))]
          : h("p", { class: "empty" }, "Không có lần chạy nào trong khoảng này.")),
      h("div", { class: "stat-grid" },
        h("div", { class: "card" }, h("h3", {}, "Theo kịch bản"),
          h("div", { class: "tbl" }, h("table", { "data-testid": "bybundle-table" },
            h("thead", {}, h("tr", {}, h("th", {}, "Kịch bản"), h("th", { title: "Số lần chạy" }, "Lần chạy"), h("th", { title: "Số lần xong chia số lần chạy đã kết thúc" }, "Tỉ lệ xong"), h("th", { title: "Thời gian trung bình một lần chạy" }, "Thời gian TB"), h("th", { title: "Tổng số lần bot dùng AI (soạn câu, đọc trang…)" }, "Lần dùng AI"))),
            h("tbody", {}, s.byBundle.length ? s.byBundle.map((b) => h("tr", {}, h("td", { class: "wrap" }, tenKbThanThien(b.bundleName)), h("td", {}, String(b.runs)), h("td", {}, pct(b.doneRate)), h("td", { class: "nowrap" }, fmtMs(b.avgMs)), h("td", {}, String(b.modelCalls))))
              : h("tr", {}, h("td", { colspan: 5, class: "muted" }, "Không có dữ liệu")))))),
        h("div", { class: "card" }, h("h3", {}, "Theo bot"),
          h("div", { class: "tbl" }, h("table", { "data-testid": "bybot-table" },
            h("thead", {}, h("tr", {}, h("th", {}, "Bot"), h("th", { title: "Số lần chạy" }, "Lần chạy"), h("th", { title: "Số lần xong chia số lần chạy đã kết thúc" }, "Tỉ lệ xong"), h("th", { title: "Lý do lỗi của lần chạy lỗi gần đây nhất" }, "Lỗi gần nhất"))),
            h("tbody", {}, s.byBot.length ? s.byBot.map((b) => h("tr", {}, h("td", { class: "name" }, h("span", { title: b.botName }, b.botName)), h("td", {}, String(b.runs)), h("td", {}, pct(b.doneRate)), h("td", { class: "reason small wrap err" }, b.lastError ?? "")))
              : h("tr", {}, h("td", { colspan: 4, class: "muted" }, "Không có dữ liệu"))))))));
  }
  function tile(label: string, value: string, cls = "", title = "", testid?: string): HTMLElement {
    return h("div", { class: "card stat-tile", title, ...(testid ? { "data-testid": testid } : {}) }, h("div", { class: "stat-label" }, label), h("div", { class: `stat-value ${cls}` }, value));
  }
  /** V9: việc THẬT bot đã làm (đếm từ Việc đã làm — chỉ ghi ở chạy thật) + bảng theo mạng. Không phụ thuộc lọc chế độ. */
  function viecThat(v: NonNullable<StatsDayDu["viec"]>): HTMLElement {
    const tong = VIEC.reduce((a, x) => a + (v.tong[x.k] ?? 0), 0) + (v.tong.khac ?? 0);
    return h("div", { class: "card", "data-testid": "stat-viec" },
      h("h3", {}, "Việc thật bot đã làm"),
      h("div", { class: "row", style: "gap:12px;align-items:stretch" }, ...VIEC.map((x) => tile(x.nhan, String(v.tong[x.k] ?? 0), x.k === "binh_luan" || x.k === "dang" ? "ok" : "", x.title, `viec-${x.k}`))),
      tong && v.theoMang.length
        ? h("div", { class: "tbl", style: "margin-top:10px" }, h("table", { "data-testid": "viec-theo-mang" },
          h("thead", {}, h("tr", {}, h("th", {}, "Mạng"), ...VIEC.map((x) => h("th", { title: x.title }, x.nhan)), h("th", { title: "Việc khác bot đã làm xong" }, "Khác"))),
          h("tbody", {}, ...v.theoMang.map((m) => h("tr", {}, h("td", { class: "nowrap" }, TEN_MANG[m.mang] ?? m.mang), ...VIEC.map((x) => h("td", {}, String(m[x.k] ?? 0))), h("td", {}, String(m.khac ?? 0)))))))
        : h("p", { class: "muted small", "data-testid": "viec-trong" }, "Chưa có việc thật nào trong khoảng này — việc chỉ được ghi khi bot Chạy thật và làm xong (đăng bài, bình luận, trả lời, vào nhóm)."),
      h("p", { class: "small" }, h("a", { href: `#/works${buildQuery({ from: from.value, to: to.value, botId: botSel.value })}`, title: "Mở Việc đã làm: từng nơi bot đã làm, bấm để mở lại xem" }, "Xem từng việc →")));
  }

  applyBtn.addEventListener("click", () => {
    history.replaceState(null, "", `#/stats${buildQuery({ from: from.value, to: to.value, botId: botSel.value, mode: modeSel.value, ownerId: ownerSel.value })}`);
    void busy(applyBtn, "Đang tải…", load);
  });
  await load();
  return () => {};
}

/** Cột chồng theo ngày: SVG thuần; khe 2px giữa các đoạn; tooltip <title>; trục nhẹ. */
function chart(days: Stats["byDay"]): SVGSVGElement {
  // viewBox theo bề rộng màn: màn hẹp dùng viewBox hẹp để chữ trục không bị co quá nhỏ.
  const W = Math.max(320, Math.min(800, (document.documentElement.clientWidth || 800) - 64)), H = W < 500 ? 200 : 240;
  const padL = 36, padB = 26, padT = 10, padR = 8;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  // Thang = vạch trên cùng của trục (bước 1/2/5×10ⁿ): nhãn nguyên, không trùng, đặt đúng chỗ; cột dùng CHUNG thang này.
  const { top: max, ticks } = yTicks(Math.max(0, ...days.map((d) => d.done + d.failed + d.halted + d.stopped)));
  const step = innerW / days.length;
  const bw = Math.max(4, Math.min(40, step * 0.7));
  const el = svg("svg", { viewBox: `0 0 ${W} ${H}`, class: "chart", role: "img", "aria-label": "Số lần chạy theo ngày", "data-testid": "byday-chart" });
  for (const v of ticks) {
    const y = padT + innerH - (innerH * v) / max;
    el.append(svg("line", { x1: padL, x2: W - padR, y1: y, y2: y, stroke: "var(--line)", "stroke-width": 1 }));
    el.append(svg("text", { x: padL - 6, y: y + 4, "text-anchor": "end", "font-size": 10, fill: "var(--muted)" }, String(v)));
  }
  days.forEach((d, i) => {
    const x = padL + step * i + (step - bw) / 2;
    let y = padT + innerH;
    const g = svg("g", { "data-testid": "day-bar" });
    const tip = `${fmtNgay(d.date)}: ${d.done} xong · ${d.failed} lỗi · ${d.halted} bị chặn / từ chối · ${d.stopped} dừng tay`;
    for (const s of SERIES) {
      const v = d[s.key];
      if (!v) continue;
      const hgt = (v / max) * innerH;
      y -= hgt;
      const r = svg("rect", { x, y: y + 1, width: bw, height: Math.max(0, hgt - 2), fill: s.color, rx: 2 });
      r.append(svg("title", {}, tip));
      g.append(r);
    }
    el.append(g);
    const label = fmtNgayNgan(d.date); // dd/MM (trước: MM-DD kiểu tháng-ngày)
    const every = Math.max(1, Math.ceil(days.length / Math.floor(innerW / 40)));
    if (i % every === 0) el.append(svg("text", { x: x + bw / 2, y: H - 8, "text-anchor": "middle", "font-size": 10, fill: "var(--muted)", "data-testid": "truc-ngay" }, label));
  });
  return el;
}
